import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { UnnamedSegment } from '@fullstory/proto/fs/services/dx-objects';
import { alphanumericUID } from '@fullstory/util/uid';
import { AppState, AppDispatch } from '../../types';
import { fullstorySelectors, unnamedSegmentsSelectors } from '../../selectors';
import { UnnamedSegmentActions } from './actions';
import { hashForUnnamedSegment } from './util';

const { getOrgId } = fullstorySelectors;
const { getUnnamedSegmentIdByHash } = unnamedSegmentsSelectors;
// The returned UnnamedSegment is not guaranteed to have been persisted on the server but will be in redux.
// The provided segment definition will be deduped with locally held unnamed segments by performing a hash
// to reduce the amount of unnamed segments that need to actually be saved.
export const saveUnnamedSegment = (
  dispatch: AppDispatch,
  getState: () => AppState,
  segmentDefinition: SegmentDefinition,
  disabled: UnnamedSegment.Disabled,
  name: string,
  description: string = '',
): UnnamedSegment => {
  const { unnamedSegment, saveUnnamedSegment } = saveUnnamedSegmentDeferred(
    getState,
    segmentDefinition,
    disabled,
    name,
    description,
  );
  if (saveUnnamedSegment) {
    dispatch(saveUnnamedSegment).catch(err =>
      console.error(
        `Failed to save unnamed segment ${unnamedSegment.id} ${JSON.stringify(
          unnamedSegment,
        )}: ${JSON.stringify(err)}.`,
      ),
    );
  }
  return unnamedSegment;
};

// saveUnnamedSegmentDeferred builds an UnnamedSegment returning an action to persist it on the backend.
// The returned UnnamedSegment will not be persisted on the server or be in redux until the saveUnnamedSegment action
// is invoked (if present). The provided segment definition will be deduped with locally held unnamed segments by
// performing a hash to reduce the amount of unnamed segments that need to actually be saved. If deduped, the returned
// saveUnnamedSegment action will be undefined as no action is necessary.
const saveUnnamedSegmentDeferred = (
  getState: () => AppState,
  segmentDefinition: SegmentDefinition,
  disabled: UnnamedSegment.Disabled,
  name: string,
  description: string = '',
) => {
  const hash = hashForUnnamedSegment(segmentDefinition, disabled, name, description);
  const idByHash = getUnnamedSegmentIdByHash(getState(), hash);

  const unnamedSegmentId = idByHash || alphanumericUID();
  const unnamedSegment: UnnamedSegment = {
    id: unnamedSegmentId,
    definition: segmentDefinition,
    disabled,
    name,
    description,
  };

  const orgId = getOrgId(getState());
  // Only return a save action if the unnamed segment did not exist locally yet.
  const saveUnnamedSegment = !idByHash
    ? UnnamedSegmentActions.saveUnnamedSegment({ orgId, unnamedSegment })
    : undefined;
  return { unnamedSegment, saveUnnamedSegment };
};
